/**
 * Constituent group picker - admin-tab flavour. Lists groups from
 * /group/search, excluding any group whose Type.Id === "preset" so admins
 * cannot recursively nest a preset inside another preset (flat rule from the
 * spec).
 *
 * Emits ids in the prefixed "Group/<short_id>" form that the API's
 * `definition.constituent_group_ids` field expects.
 *
 * Note: site-admins see every group the API returns (V2 visibility checks
 * still apply server-side; the admin endpoint bypasses them anyway).
 */
import { useEffect, useMemo, useState } from "react";
import {
  IonCheckbox,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonSearchbar,
  IonSpinner,
} from "@ionic/react";
import {
  chevronDownOutline,
  closeOutline,
  peopleOutline,
} from "ionicons/icons";
import { useQuery } from "@tanstack/react-query";
import { usePymbleApi } from "../../hooks/usePymbleApi";

import "./presets.css";

/**
 * Server-side result cap for the picker. /group/search returns the whole
 * active catalogue (~12k groups / 10.7 MB, 13.7 s cold - measured against
 * papi-staging 2026-08-28) when asked for it unbounded, so every fetch here
 * carries `limit`. The API's `page` is 1-based and left at its default; the
 * picker narrows with `search` rather than paging.
 */
export const CONSTITUENT_PICKER_LIMIT = 50;

/**
 * Delay between the last keystroke and the request, matching the debounced
 * /group/search pattern used elsewhere in the admin tabs.
 */
export const CONSTITUENT_SEARCH_DEBOUNCE_MS = 300;

interface GroupRow {
  _id: string;
  Name?: { Short?: string; Long?: string };
  Type?: { Id?: number | string; Label?: string };
}

interface ConstituentGroupPickerProps {
  /** Selected ids in "Group/<short>" form. */
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** Opt-in compact variant (Create-preset redesign): searchbar + trailing
   *  chevron, chips + "N groups selected" count, and a results panel that
   *  stays collapsed until focused/typed/toggled. Defaults to the legacy
   *  always-open layout so EditPresetModal is unaffected. */
  collapsible?: boolean;
  /** Response-only `{"Group/<id>": Name.Short}` map (the API's
   *  `definition.constituent_group_labels`). Seeds chip labels for ids that
   *  were saved earlier: the picker only ever holds one bounded page of
   *  groups, so a pre-saved constituent is usually not in the current result
   *  set and would otherwise render the generic placeholder. */
  initialLabels?: Record<string, string>;
}

export function ConstituentGroupPicker({
  selectedIds,
  onChange,
  collapsible = false,
  initialLabels,
}: ConstituentGroupPickerProps) {
  const { callApi, isAuthenticated } = usePymbleApi();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Collapsible variant: hide the list until the user engages with it.
  const [resultsOpen, setResultsOpen] = useState(false);

  // Debounce the keystroke -> query-key hop so typing does not fire a request
  // per character. The empty-search case settles immediately: it is the idle
  // listing, not a search.
  useEffect(() => {
    const trimmed = search.trim();
    if (!trimmed) {
      setDebouncedSearch("");
      return;
    }
    const handle = setTimeout(
      () => setDebouncedSearch(trimmed),
      CONSTITUENT_SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(handle);
  }, [search]);

  // Server-side search + a hard `limit`, so the picker fetches a bounded
  // result set rather than the whole catalogue. Matching is the API's, which
  // is why there is no client-side filter left: filtering the server's own
  // matches again could only ever hide rows it deliberately returned.
  const {
    data: groups,
    isLoading,
    isFetching,
  } = useQuery<GroupRow[]>({
    queryKey: ["preset-constituent-groups", debouncedSearch],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      params.set("active_only", "1");
      params.set("limit", String(CONSTITUENT_PICKER_LIMIT));
      const res = await callApi("GET", `/group/search?${params.toString()}`);
      if (!Array.isArray(res)) return [];
      // Drop preset-typed groups - the flat-only rule means a preset's
      // constituent set must never include other presets. The API blocks
      // this anyway (V3 in the spec) but pre-filtering avoids dangling
      // 422s in the UI.
      return (res as GroupRow[]).filter(
        (g) => String(g.Type?.Id ?? "") !== "preset"
      );
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5,
  });

  // True from the first keystroke until the matching results are in hand -
  // the debounce window included. The rows on screen belong to the PREVIOUS
  // query during that window, and they stay clickable unless we say so, which
  // is how an operator ends up selecting a group that does not match the text
  // in the box. The old client-side filter could never disagree with the
  // searchbar; a server-side one can, so the pending state has to be honest.
  const searchPending = search.trim() !== debouncedSearch || isFetching;

  // Chip labels survive the result set turning over: seed from the API's
  // label map, then stash every id -> name pair the picker sees.
  const [labelStore, setLabelStore] = useState<Record<string, string>>(
    () => ({ ...(initialLabels ?? {}) })
  );

  // Merge a later re-seed (parent refetch) WITHOUT clobbering in-session
  // discoveries.
  useEffect(() => {
    if (!initialLabels) return;
    setLabelStore((prev) => {
      let mutated = false;
      const next = { ...prev };
      for (const [key, value] of Object.entries(initialLabels)) {
        if (value && next[key] === undefined) {
          next[key] = value;
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
  }, [initialLabels]);

  useEffect(() => {
    if (!groups?.length) return;
    setLabelStore((prev) => {
      let mutated = false;
      const next = { ...prev };
      for (const row of groups) {
        const key = `Group/${row._id}`;
        const label = row.Name?.Short ?? row.Name?.Long;
        if (label && next[key] !== label) {
          next[key] = label;
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
  }, [groups]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const rows = groups ?? [];

  const toggle = (shortId: string) => {
    const prefixed = `Group/${shortId}`;
    if (selectedSet.has(prefixed)) {
      onChange(selectedIds.filter((id) => id !== prefixed));
    } else {
      onChange([...selectedIds, prefixed]);
    }
  };

  const removeChip = (prefixed: string) => {
    onChange(selectedIds.filter((id) => id !== prefixed));
  };

  // Chip label - prefer a known name, fall back to "Group" if the id hails
  // from outside anything this session has seen (edit-mode edge case where
  // the API could not resolve the label either).
  const chipLabel = (prefixed: string): string => labelStore[prefixed] ?? "Group";

  // Collapsible variant hides the list until the user engages. resultsOpen is
  // the single source of truth so the chevron can always collapse it; starting
  // a search auto-reveals it (see the searchbar handler). The legacy layout
  // keeps the panel permanently open.
  const showResults = !collapsible || resultsOpen;

  const chipsBlock = selectedIds.length > 0 && (
    <div
      className="preset-picker-chips"
      aria-label={`${selectedIds.length} groups selected`}
    >
      {selectedIds.map((prefixed) => (
        <span key={prefixed} className="preset-picker-chip">
          {chipLabel(prefixed)}
          <button
            type="button"
            className="preset-picker-chip-remove"
            aria-label={`Remove ${chipLabel(prefixed)}`}
            onClick={() => removeChip(prefixed)}
          >
            <IonIcon icon={closeOutline} />
          </button>
        </span>
      ))}
    </div>
  );

  const resultsBlock = (
    <div className="preset-picker-results">
      {isLoading || searchPending ? (
        <div className="preset-picker-empty">
          <IonSpinner name="dots" />
          <p>Loading groups...</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="preset-picker-empty">
          <IonIcon icon={peopleOutline} aria-hidden="true" />
          <p>No matching groups</p>
        </div>
      ) : (
        <IonList lines="inset">
          {rows.map((row) => {
            const checked = selectedSet.has(`Group/${row._id}`);
            return (
              <IonItem
                key={row._id}
                button
                onClick={() => toggle(row._id)}
                className="preset-picker-result-item"
              >
                <IonIcon icon={peopleOutline} slot="start" aria-hidden="true" />
                <IonLabel>
                  <h3>{row.Name?.Short ?? row.Name?.Long ?? "(unnamed)"}</h3>
                  {row.Type?.Label && <p>{row.Type.Label}</p>}
                </IonLabel>
                <IonCheckbox
                  slot="end"
                  checked={checked}
                  aria-label={`Select ${row.Name?.Short ?? "group"}`}
                  onClick={(e) => e.stopPropagation()}
                  onIonChange={(e) => {
                    if (e.detail.checked !== checked) toggle(row._id);
                  }}
                />
              </IonItem>
            );
          })}
        </IonList>
      )}
    </div>
  );

  if (!collapsible) {
    return (
      <div className="preset-picker">
        <IonSearchbar
          className="preset-picker-searchbar"
          value={search}
          onIonInput={(e) => setSearch(e.detail.value ?? "")}
          placeholder="Search your groups"
          debounce={150}
          showClearButton="focus"
        />
        {chipsBlock}
        {resultsBlock}
      </div>
    );
  }

  return (
    <div className="preset-picker preset-picker--collapsible">
      <div className="preset-picker-searchrow">
        <IonSearchbar
          className="preset-picker-searchbar"
          value={search}
          onIonInput={(e) => {
            const value = e.detail.value ?? "";
            if (search.trim() === "" && value.trim() !== "")
              setResultsOpen(true);
            setSearch(value);
          }}
          onIonFocus={() => setResultsOpen(true)}
          placeholder="Search your groups"
          debounce={150}
          showClearButton="focus"
        />
        <button
          type="button"
          className="preset-picker-toggle"
          aria-expanded={showResults}
          aria-label={showResults ? "Hide groups" : "Show groups"}
          onClick={() => setResultsOpen((o) => !o)}
        >
          <IonIcon
            icon={chevronDownOutline}
            className={showResults ? "preset-picker-toggle-icon open" : "preset-picker-toggle-icon"}
          />
        </button>
      </div>
      {chipsBlock}
      {selectedIds.length > 0 && (
        <div className="preset-picker-count">
          {selectedIds.length} group{selectedIds.length === 1 ? "" : "s"}{" "}
          selected
        </div>
      )}
      {showResults && resultsBlock}
    </div>
  );
}
